import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { connect } from "react-redux";
import { Dispatch, bindActionCreators } from "redux";
import slugify from "slugify";
import {
  TextField,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Label,
  Toggle,
  Dropdown,
  Icon,
} from "office-ui-fabric-react";
import "react-sortable-tree/style.css";
import * as _yup from "yup";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";

import { DEFAULT_SCHEMA } from "app/shared/constants";
import { MetaTypeEnum } from "app/shared";
import { RootState } from "app/redux/reducer";
import {
  setPageData,
  setPageErrors,
  initialPageData,
} from "app/redux/page/actions";
import { toggleModal } from "app/redux/modal/actions";
import { showMessage, toggleConfirmDialog } from "app/redux/system/actions";
import {
  getActivityTypesForLocal,
  IActivityType,
} from "app/services/activity-type";
import {
  getActivityGroupsForLocal,
  IActivityGroup,
} from "app/services/activity-group";
import {
  IActivityCommunity,
  getActivityCommunitiesForLocal,
} from "app/services/activity-community";

import PageTitle from "components/customs/PageTitle";
import { castThunkAction } from "app/utils/casting";
import {
  createActivity,
  IActivity,
  IActivityRule,
} from "app/services/activity";
import Sidebar from "components/customs/Sidebar/Sidebar";
import PageLayout from "containers/PageLayout";
import FormAccordion from "components/customs/FormAccordion";
import MetaEditor from "components/customs/MetaFields/Fields/MetaEditor";
import MetaSingleFile from "components/customs/MetaFields/Fields/MetaSingleFile";
import MetaPhotoGallery from "components/customs/MetaFields/Fields/MetaPhotoGallery";
import { Designer } from "components/customs/Designer";
import { MessageType } from "app/redux/system/types";
import { ReactSortable } from "react-sortablejs";

function mapStateToProps(state: RootState, ownProps: RouteComponentProps) {
  return {
    ...ownProps,
    defaultLanguage: state.system.defaultLanguage,
    language: state.system.language,
    pageData: state.page.pageData as IActivity,
    pageErrors: state.page.pageErrors,
    contentTypes: state.contentType.contentTypes.list,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    ...bindActionCreators(
      {
        setPageData,
        setPageErrors,
        showMessage,
        initialPageData,
        createActivity,
        getActivityTypesForLocal,
        getActivityGroupsForLocal,
        getActivityCommunitiesForLocal,
        toggleModal,
        toggleConfirmDialog,
      },
      dispatch
    ),
  };
}

type CreateActivityProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

type ActiveTexts = {
  dateString: boolean;
  timeDescription: boolean;
  listNote: boolean;
  artistString: boolean;
  activityRuleString: boolean;
  calendarString: boolean;
};

interface OwnState {
  messageType: MessageBarType;
  activityTypes: IActivityType[];
  activityGroups: IActivityGroup[];
  activityCommunity: IActivityCommunity[];
  activeTexts: ActiveTexts;
  sortableMode: boolean;
  activeAccordion: number;
}

class CreateActivity extends React.Component<CreateActivityProps, OwnState> {
  state = {
    messageType: MessageBarType.warning,
    activityTypes: [] as IActivityType[],
    activityGroups: [] as IActivityGroup[],
    activityCommunity: [] as IActivityCommunity[],
    activeTexts: {
      dateString: false,
      timeDescription: false,
      listNote: false,
      artistString: false,
      activityRuleString: false,
      calendarString: false,
    },
    sortableMode: false,
    activeAccordion: -1,
  };

  componentDidMount() {
    this.resetData();
    this.getActivityTypes();
    this.getActivityGroups();
    this.getActivityCommunities();
  }

  resetData() {
    this.setState(
      {
        messageType: MessageBarType.warning,
      },
      () => {
        this.props.initialPageData<IActivity>({
          activityGroupId: "",
          activityCommunityId: "",
          activityTypeId: "",
          otherActivityTypeIds: [],
          oldKeys: [],
          durationString: "",
          calendarString: "",
          adminTitle: "",
          description: "",
          gallery: [],
          highlighted: false,
          isFree: false,
          isFreePrivate: false,
          listNote: "",
          dateString: "",
          timeDescription: "",
          activityRuleString: "",
          searchString: "",
          artistTitle: "",
          artistString: "",
          integrationId: "",
          dateOrder: 0,
          key: "",
          language: this.props.defaultLanguage,
          order: 30,
          relationId: "",
          schema: DEFAULT_SCHEMA,
          shortDescription: "",
          title: "",
          subTitle: "",
          secondSubTitle: "",
          heroTitle: "",
          establishmentTitle: "",
          placeDescription: "",
          ticketLink: "",
          rules: [],
          heroSlider: [],
          isActive: true,
        });
      }
    );
  }

  getActivityTypes() {
    castThunkAction<IActivityType[]>(
      this.props.getActivityTypesForLocal(this.props.pageData.language || "tr")
    ).then((response) => {
      this.setState({
        activityTypes: response || [],
      });
    });
  }

  getActivityGroups() {
    castThunkAction<IActivityGroup[]>(
      this.props.getActivityGroupsForLocal(this.props.pageData.language || "tr")
    ).then((response) => {
      this.setState({
        activityGroups: response || [],
      });
    });
  }

  getActivityCommunities() {
    castThunkAction<IActivityCommunity[]>(
      this.props.getActivityCommunitiesForLocal(
        this.props.pageData.language || "tr"
      )
    ).then((response) => {
      this.setState({
        activityCommunity: response || [],
      });
    });
  }

  renderTextByMessageType() {
    switch (this.state.messageType) {
      case MessageBarType.success:
        return "Kaydetme işlemi başarıyla gerçekleşmiştir. Düzenleme sayfasına yönlendiriliyorsunuz...";
      case MessageBarType.error:
        return "Kayıt işlemi yapılırken bir hata oluşmuştur. Lütfen servis sağlayıcınızla iletişime geçin.";
      default:
        return null;
    }
  }

  renderMessageBar() {
    if (this.state.messageType !== MessageBarType.warning)
      return (
        <MessageBar messageBarType={this.state.messageType} isMultiline={false}>
          {this.renderTextByMessageType()}
        </MessageBar>
      );
    return null;
  }

  onValidate() {
    const data = this.props.pageData;

    return new Promise((resolve) => {
      let validationSchema = _yup.object<object>({
        title: _yup.string().required("Bir etkinlik adı girmelisiniz"),
        activityTypeId: _yup
          .string()
          .required("Bir etkinlik türü seçmelisiniz"),
      });

      let formErrors: Record<string, string> = {};

      validationSchema
        .validate(data, {
          abortEarly: false,
        })
        .then(() => {
          resolve(true);
        })
        .catch((err) => {
          err.inner.forEach((e: any) => {
            const { path, message } = e;

            formErrors[path] = message;
          });

          resolve(formErrors);
        });
    });
  }

  onSubmit() {
    this.onValidate().then((resolved) => {
      if (typeof resolved === "boolean" && resolved) {
        castThunkAction<IActivity>(
          this.props.createActivity(this.props.pageData)
        ).then((res) => {
          this.setState(
            {
              messageType: res?.id
                ? MessageBarType.success
                : MessageBarType.error,
            },
            () => {
              if (res?.id) {
                setTimeout(() => {
                  this.props.history.push(`/activity/edit/${res.id}`);
                }, 1200);
              }
            }
          );
        });
      } else {
        this.props.showMessage(
          "Aşağıdaki bilgilerde eksik ya da hatalı bilgiler bulunmaktadır. Lütfen bilgilerinizi kontrol ediniz.",
          "Eksik veya hatalı bilgiler mevcut",
          MessageType.ERROR,
          () => {
            return (
              <div className="error-list">
                {_.keys(resolved).map((item, index) => (
                  <div key={index} className="error-item">
                    {(resolved as Record<string, any>)[item]}
                  </div>
                ))}
              </div>
            );
          }
        );
      }
    });
  }

  onToggleText(key: string, checked: boolean) {
    this.setState(
      {
        activeTexts: {
          ...this.state.activeTexts,
          [key]: checked,
        },
      },
      () => {
        if (!checked) {
          this.props.setPageData(key, "");
        }
      }
    );
  }

  onChangeOtherActivityTypes = (event: any, option: any) => {
    const selectedKeys = this.props.pageData.otherActivityTypeIds || [];
    if (option.selected) {
      selectedKeys.push(option.key);
    } else {
      const index = selectedKeys.indexOf(option.key);
      if (index > -1) {
        selectedKeys.splice(index, 1);
      }
    }
    this.props.setPageData("otherActivityTypeIds", selectedKeys);
  };

  renderForm() {
    return (
      <div className="form-groups row">
        <div className="form-group col-md-12" style={{ paddingTop: 0 }}>
          <FormAccordion showContent={true} title="Genel Bilgiler">
            <div className="form-wrapper row" style={{ padding: "10px 5px" }}>
              <div className="item row col-md-6">
                <TextField
                  className="custom-textfield"
                  label="Etkinlik Admin Adı"
                  value={this.props.pageData.adminTitle}
                  onChange={(event: any) => {
                    this.props.setPageData("adminTitle", event.target.value);
                  }}
                />
              </div>
              <div className="item row col-md-6">
                <TextField
                  className="custom-textfield"
                  label="Etkinlik Anahtarı"
                  value={this.props.pageData.key}
                  onChange={(event: any) => {
                    this.props.setPageData("key", event.target.value);
                  }}
                />
              </div>
              <div className="item row col-md-6">
                <Dropdown
                  className="custom-dropdown"
                  calloutProps={{
                    className: "custom-dropdown-callout",
                  }}
                  label="Etkinlik Grubu"
                  options={this.state.activityGroups.map((activityGroup) => ({
                    key: activityGroup.id as string,
                    text: activityGroup.title,
                  }))}
                  onChange={(_, opt) => {
                    this.props.setPageData("activityGroupId", opt?.key);
                  }}
                  selectedKey={this.props.pageData.activityGroupId}
                />
              </div>
              <div className="item row col-md-6">
                <Dropdown
                  className="custom-dropdown"
                  calloutProps={{
                    className: "custom-dropdown-callout",
                  }}
                  label="Etkinlik Topluluğu"
                  options={this.state.activityCommunity.map(
                    (activityCommunity) => ({
                      key: activityCommunity.id as string,
                      text: activityCommunity.title,
                    })
                  )}
                  onChange={(_, opt) => {
                    this.props.setPageData("activityCommunityId", opt?.key);
                  }}
                  selectedKey={this.props.pageData.activityCommunityId}
                />
              </div>
              <div className="item row col-md-6">
                <Dropdown
                  className="custom-dropdown"
                  calloutProps={{
                    className: "custom-dropdown-callout",
                  }}
                  label="Etkinlik Türü"
                  options={this.state.activityTypes.map((activityType) => ({
                    key: activityType.id as string,
                    text: activityType.title,
                  }))}
                  onChange={(_, opt) => {
                    this.props.setPageData("activityTypeId", opt?.key);
                  }}
                  selectedKey={this.props.pageData.activityTypeId}
                />
              </div>
              <div className="item row col-md-6">
                <Dropdown
                  multiSelect
                  className="custom-dropdown"
                  calloutProps={{
                    className: "custom-dropdown-callout",
                  }}
                  label="Diğer Etkinlik Türleri"
                  options={this.state.activityTypes.map((activityType) => ({
                    key: activityType.id as string,
                    text: activityType.title,
                  }))}
                  onChange={this.onChangeOtherActivityTypes}
                  selectedKeys={this.props.pageData.otherActivityTypeIds}
                />
              </div>
              <div className="item row col-md-4" style={{ paddingTop: 8 }}>
                <Toggle
                  className="custom-toggle m-left-auto"
                  onChange={(_, checked) =>
                    this.props.setPageData("highlighted", checked)
                  }
                  checked={this.props.pageData.highlighted}
                  label="Etkinlik Öne Çıkartılsın Mı?"
                  onText="Evet"
                  offText="Hayır"
                />
              </div>
              <div className="item row col-md-4" style={{ paddingTop: 8 }}>
                <Toggle
                  className="custom-toggle m-left-auto"
                  onChange={(_, checked) =>
                    this.props.setPageData("isFree", checked)
                  }
                  checked={this.props.pageData.isFree}
                  label="Etkinlik Ücretsiz Mi?"
                />
              </div>
              <div className="item row col-md-4" style={{ paddingTop: 8 }}>
                <Toggle
                  className="custom-toggle m-left-auto"
                  onChange={(_, checked) =>
                    this.props.setPageData("isFreePrivate", checked)
                  }
                  checked={this.props.pageData.isFreePrivate}
                  label="Etkinlik Ücretsiz Olup, Ücretsiz Bilgisi Gizlensin Mi?"
                />
              </div>
              <div className="item row col-md-6">
                <TextField
                  className="custom-textfield"
                  label="Etkinlik Hero Başlık"
                  value={this.props.pageData.heroTitle}
                  onChange={(event: any) => {
                    this.props.setPageData("heroTitle", event.target.value);
                  }}
                />
              </div>
              <div className="item row col-md-6">
                <TextField
                  className="custom-textfield"
                  label="Etkinlik Alt Başlık (Hero Üzerinde)"
                  value={this.props.pageData.subTitle}
                  onChange={(event: any) => {
                    this.props.setPageData("subTitle", event.target.value);
                  }}
                />
              </div>
              <div className="item row col-md-6">
                <TextField
                  className="custom-textfield"
                  label="Kurum Adı"
                  value={this.props.pageData.establishmentTitle}
                  onChange={(event: any) => {
                    this.props.setPageData(
                      "establishmentTitle",
                      event.target.value
                    );
                  }}
                />
              </div>
              <div className="item row col-md-6">
                <TextField
                  className="custom-textfield"
                  label="Etkinlik 2. Alt Başlık"
                  value={this.props.pageData.secondSubTitle}
                  onChange={(event: any) => {
                    this.props.setPageData(
                      "secondSubTitle",
                      event.target.value
                    );
                  }}
                />
              </div>
              <div className="item row col-md-12">
                <TextField
                  className="custom-textfield"
                  label="Etkinlik Besteci / Yazar Bilgileri"
                  value={this.props.pageData.artistTitle}
                  onChange={(event: any) => {
                    this.props.setPageData("artistTitle", event.target.value);
                  }}
                />
              </div>
              <div className="item row col-md-6">
                <TextField
                  className="custom-textfield"
                  label="Etkinlik Mekan Ek Açıklaması"
                  value={this.props.pageData.placeDescription}
                  onChange={(event: any) => {
                    this.props.setPageData(
                      "placeDescription",
                      event.target.value
                    );
                  }}
                />
              </div>
              <div className="item row col-md-6">
                <TextField
                  className="custom-textfield"
                  label="Etkinlik Zamanı"
                  value={this.props.pageData.durationString}
                  onChange={(event: any) => {
                    this.props.setPageData(
                      "durationString",
                      event.target.value
                    );
                  }}
                  placeholder="ÖR: 2 Perde / 2 Saat 24 Dakika"
                />
              </div>
              <div className="item row col-md-12">
                <Toggle
                  label="Bilet satışta açıldı mı?"
                  checked={this.props.pageData.isActive}
                  onChange={(_, checked) =>
                    this.props.setPageData("isActive", checked)
                  }
                />
              </div>
              <div className="item row col-md-12">
                <TextField
                  className="custom-textfield"
                  label="Etkinlik Biletlendirme Linki"
                  value={this.props.pageData.ticketLink}
                  onChange={(event: any) => {
                    this.props.setPageData("ticketLink", event.target.value);
                  }}
                  multiline={true}
                  resizable={false}
                />
              </div>
              <div className="item row col-md-12">
                <TextField
                  className="custom-textfield"
                  label="Etkinlik Kısa Açıklaması"
                  value={this.props.pageData.shortDescription}
                  onChange={(event: any) => {
                    this.props.setPageData(
                      "shortDescription",
                      event.target.value
                    );
                  }}
                  multiline={true}
                  resizable={false}
                />
              </div>
              <div className="item row col-md-12">
                <TextField
                  className="custom-textfield"
                  label="Etkinlik Aranacak Kelimeler & Metin"
                  value={this.props.pageData.searchString}
                  onChange={(event: any) => {
                    this.props.setPageData("searchString", event.target.value);
                  }}
                  multiline={true}
                  resizable={false}
                />
              </div>
              <div className="item row col-md-6">
                <Label>
                  Etkinlik Özel Tarih Metni
                  <Toggle
                    className="custom-toggle m-left-auto"
                    onChange={(_, checked) =>
                      this.onToggleText("dateString", checked || false)
                    }
                    checked={this.state.activeTexts.dateString}
                    label="Kullanılsın mı?"
                    styles={{ pill: { marginRight: "5px !important" } }}
                    inlineLabel={true}
                  />
                </Label>
                {this.state.activeTexts.dateString && (
                  <TextField
                    className="custom-textfield"
                    value={this.props.pageData.dateString}
                    onChange={(event: any) => {
                      this.props.setPageData("dateString", event.target.value);
                    }}
                    multiline={true}
                    resizable={false}
                    placeholder="ÖR: Hafta içi her gün"
                  />
                )}
              </div>
              <div className="item row col-md-6">
                <Label>
                  Etkinlik Özel Zaman / Periyod Metni
                  <Toggle
                    className="custom-toggle m-left-auto"
                    onChange={(_, checked) =>
                      this.onToggleText("timeDescription", checked || false)
                    }
                    checked={this.state.activeTexts.timeDescription}
                    label="Kullanılsın mı?"
                    styles={{ pill: { marginRight: "5px !important" } }}
                    inlineLabel={true}
                  />
                </Label>
                {this.state.activeTexts.timeDescription && (
                  <TextField
                    className="custom-textfield"
                    value={this.props.pageData.timeDescription}
                    onChange={(event: any) => {
                      this.props.setPageData(
                        "timeDescription",
                        event.target.value
                      );
                    }}
                    multiline={true}
                    resizable={false}
                    placeholder="ÖR: 08:00 - 12:00 ve 13:00 - 17:00"
                  />
                )}
              </div>
              <div className="item row col-md-12">
                <Label>
                  Etkinlik Liste Notu
                  <Toggle
                    className="custom-toggle m-left-auto"
                    onChange={(_, checked) =>
                      this.onToggleText("listNote", checked || false)
                    }
                    checked={this.state.activeTexts.listNote}
                    label="Kullanılsın mı?"
                    styles={{ pill: { marginRight: "5px !important" } }}
                    inlineLabel={true}
                  />
                </Label>
                {this.state.activeTexts.listNote && (
                  <TextField
                    className="custom-textfield"
                    value={this.props.pageData.listNote}
                    onChange={(event: any) => {
                      this.props.setPageData("listNote", event.target.value);
                    }}
                    multiline={true}
                    resizable={false}
                  />
                )}
              </div>
              <div className="item row col-md-12">
                <Label>
                  Etkinlik Sanatçı Bilgisi
                  <Toggle
                    className="custom-toggle m-left-auto"
                    onChange={(_, checked) =>
                      this.onToggleText("artistString", checked || false)
                    }
                    checked={this.state.activeTexts.artistString}
                    label="Kullanılsın mı?"
                    styles={{ pill: { marginRight: "5px !important" } }}
                    inlineLabel={true}
                  />
                </Label>
                {this.state.activeTexts.artistString && (
                  <MetaEditor
                    type={MetaTypeEnum.Editor}
                    onChange={(value: string) => {
                      this.props.setPageData("artistString", value);
                    }}
                    meta={{
                      data: this.props.pageData.artistString,
                      type: MetaTypeEnum.Editor,
                      title: "",
                      column: 12,
                      name: "",
                    }}
                    toggleModal={toggleModal}
                  />
                )}
              </div>
              <div className="item row col-md-12">
                <Label>
                  Etkinlik Kuralları Bilgisi
                  <Toggle
                    className="custom-toggle m-left-auto"
                    onChange={(_, checked) =>
                      this.onToggleText("activityRuleString", checked || false)
                    }
                    checked={this.state.activeTexts.activityRuleString}
                    label="Kullanılsın mı?"
                    styles={{ pill: { marginRight: "5px !important" } }}
                    inlineLabel={true}
                  />
                </Label>
                {this.state.activeTexts.activityRuleString && (
                  <MetaEditor
                    type={MetaTypeEnum.Editor}
                    onChange={(value: string) => {
                      this.props.setPageData("activityRuleString", value);
                    }}
                    meta={{
                      data: this.props.pageData.activityRuleString,
                      type: MetaTypeEnum.Editor,
                      title: "",
                      column: 12,
                      name: "",
                    }}
                    toggleModal={toggleModal}
                  />
                )}
              </div>
              <div className="item row col-md-12">
                <Label>
                  Etkinlik Takvim Açıklaması
                  <Toggle
                    className="custom-toggle m-left-auto"
                    onChange={(_, checked) =>
                      this.onToggleText("calendarString", checked || false)
                    }
                    checked={this.state.activeTexts.calendarString}
                    label="Kullanılsın mı?"
                    styles={{ pill: { marginRight: "5px !important" } }}
                    inlineLabel={true}
                  />
                </Label>
                {this.state.activeTexts.calendarString && (
                  <MetaEditor
                    type={MetaTypeEnum.Editor}
                    onChange={(value: string) => {
                      this.props.setPageData("calendarString", value);
                    }}
                    meta={{
                      data: this.props.pageData.calendarString,
                      type: MetaTypeEnum.Editor,
                      title: "",
                      column: 12,
                      name: "",
                    }}
                    toggleModal={toggleModal}
                  />
                )}
              </div>
              <div className="item row col-md-12">
                <Label>Etkinlik Açıklaması</Label>
                <MetaEditor
                  type={MetaTypeEnum.Editor}
                  onChange={(value: string) => {
                    this.props.setPageData("description", value);
                  }}
                  meta={{
                    data: this.props.pageData.description,
                    type: MetaTypeEnum.Editor,
                    title: "",
                    column: 12,
                    name: "",
                  }}
                  toggleModal={toggleModal}
                />
              </div>
              <div className="item row col-md-12">
                <Label>Fotoğraf Galerisi</Label>
                <MetaPhotoGallery
                  type={MetaTypeEnum.PhotoGallery}
                  onChange={(value) => {
                    this.props.setPageData("gallery", value);
                  }}
                  meta={{
                    data: this.props.pageData.gallery,
                    type: MetaTypeEnum.PhotoGallery,
                    title: "",
                    column: 12,
                    name: "",
                  }}
                  toggleModal={toggleModal}
                />
              </div>
            </div>
          </FormAccordion>
        </div>
      </div>
    );
  }

  renderRuleItems() {
    return (this.props.pageData.rules || []).map(
      (rule: IActivityRule, index: number) => (
        <div
          key={index}
          className={
            this.state.activeAccordion === index
              ? "fa-Accordion-item active"
              : "fa-Accordion-item"
          }
        >
          <div className="title row">
            <div
              onClick={() => {
                this.setState({
                  activeAccordion:
                    this.state.activeAccordion !== index ? index : -1,
                });
              }}
              className="text col no-padding"
            >
              {rule.title || `Kural ${index + 1}`}
            </div>
            <Icon
              onClick={() => {
                const rules = [...this.props.pageData.rules];

                rules.splice(index, 1);
                this.props.setPageData("rules", rules);
              }}
              className="m-left-auto remove-accordion"
              iconName="ChromeClose"
            />
          </div>
          {this.state.activeAccordion === index && (
            <div className="content">
              <div className="item row col-md-12">
                <TextField
                  className="custom-textfield"
                  label="Başlık"
                  value={rule.title}
                  onChange={(event: any) => {
                    const rules = [...this.props.pageData.rules];

                    rules[index].title = event.target.value;
                    this.props.setPageData("rules", rules);
                  }}
                />
              </div>
              <div className="item row col-md-12">
                <Label>İçerik</Label>
                <MetaEditor
                  type={MetaTypeEnum.Editor}
                  onChange={(value: string) => {
                    const rules = [...this.props.pageData.rules];

                    rules[index].content = value;
                    this.props.setPageData("rules", rules);
                  }}
                  meta={{
                    data: rule.content,
                    type: MetaTypeEnum.Editor,
                    title: "",
                    column: 12,
                    name: "",
                  }}
                  toggleModal={toggleModal}
                />
              </div>
            </div>
          )}
        </div>
      )
    );
  }

  renderRules() {
    return (
      <div className="form-groups row">
        <div className="form-group col-md-12" style={{ paddingTop: 0 }}>
          <FormAccordion showContent={true} title="Etkinlik Kural Bilgileri">
            <div className="form-wrapper row" style={{ padding: "15px 20px" }}>
              <div className="accordion-wrapper-header">
                <div className="accordion-wrapper-header-title">Kurallar</div>
                <PrimaryButton
                  iconProps={{ iconName: "CalculatorAddition" }}
                  className="custom-button"
                  text="Yeni Ekle"
                  onClick={() => {
                    this.props.setPageData("rules", [
                      ...this.props.pageData.rules,
                      { id: uuidv4(), title: "", content: "" },
                    ]);
                  }}
                  styles={{
                    root: {
                      fontSize: 11,
                      background: "#33926e !important",
                      borderColor: "#33926e !important",
                    },
                  }}
                />
                <Toggle
                  className="custom-toggle m-left-auto"
                  onChange={(_, checked) =>
                    this.setState({ sortableMode: checked as boolean })
                  }
                  checked={this.state.sortableMode}
                  label="Sıralama Modu"
                  inlineLabel
                  styles={{
                    root: {
                      marginBottom: "-5px",
                      marginTop: "-1px !important",
                    },
                    container: {
                      marginTop: "-1px !important",
                    },
                  }}
                />
              </div>
              <div className="fa-Accordion accordion-wrapper-content">
                {this.state.sortableMode ? (
                  <ReactSortable
                    list={this.props.pageData.rules || []}
                    setList={(rules: IActivityRule[]) =>
                      this.props.setPageData("rules", rules)
                    }
                    animation={150}
                  >
                    {this.renderRuleItems()}
                  </ReactSortable>
                ) : (
                  this.renderRuleItems()
                )}
              </div>
            </div>
          </FormAccordion>
        </div>
      </div>
    );
  }

  renderTitle() {
    const { pageData, pageErrors, initialPageData } = this.props;

    return (
      <div className="post-title">
        <TextField
          placeholder="Etkinlik Başlığı Giriniz"
          value={pageData.title || ""}
          className="custom-textfield border"
          errorMessage={pageErrors.title}
          onChange={(event: any) => {
            initialPageData({
              ...this.props.pageData,
              title: event.target.value,
              adminTitle: event.target.value,
              heroTitle: event.target.value,
              key: slugify(event.target.value, {
                lower: true,
                replacement: "-",
                strict: true,
              }),
            });
          }}
        />
      </div>
    );
  }

  renderImages() {
    return (
      <div className="form-groups row">
        <div className="form-group col-md-12" style={{ paddingTop: 0 }}>
          <FormAccordion showContent={true} title="Dosya Ayarları">
            <div className="form-wrapper row" style={{ padding: "10px 5px" }}>
              <div className="item row col-md-12">
                <Label>Ana (Hero) Görsel</Label>
                <MetaSingleFile
                  type={MetaTypeEnum.SingleImage}
                  onChange={(value) => {
                    this.props.setPageData("profileImageHero", value);
                  }}
                  meta={{
                    data: this.props.pageData.profileImageHero,
                    type: MetaTypeEnum.SingleImage,
                    title: "",
                    column: 12,
                    name: "",
                  }}
                  toggleModal={toggleModal}
                />
              </div>
              <div className="item row col-md-12">
                <Label>Hero Slider</Label>
                <MetaPhotoGallery
                  type={MetaTypeEnum.PhotoGallery}
                  onChange={(value) => {
                    this.props.setPageData("heroSlider", value);
                  }}
                  meta={{
                    data: this.props.pageData.heroSlider,
                    type: MetaTypeEnum.PhotoGallery,
                    title: "",
                    column: 12,
                    name: "",
                  }}
                  toggleModal={toggleModal}
                  className="image-gallery-right"
                />
              </div>
              <div className="item row col-md-12">
                <Label>Mekan Görsel</Label>
                <MetaSingleFile
                  type={MetaTypeEnum.SingleImage}
                  onChange={(value) => {
                    this.props.setPageData("placeImage", value);
                  }}
                  meta={{
                    data: this.props.pageData.placeImage,
                    type: MetaTypeEnum.SingleImage,
                    title: "",
                    column: 12,
                    name: "",
                  }}
                  toggleModal={toggleModal}
                />
              </div>
              <div className="item row col-md-12">
                <Label>Yatay Görsel</Label>
                <MetaSingleFile
                  type={MetaTypeEnum.SingleImage}
                  onChange={(value) => {
                    this.props.setPageData("profileImageHorizontal", value);
                  }}
                  meta={{
                    data: this.props.pageData.profileImageHorizontal,
                    type: MetaTypeEnum.SingleImage,
                    title: "",
                    column: 12,
                    name: "",
                  }}
                  toggleModal={toggleModal}
                />
              </div>
              <div className="item row col-md-12">
                <Label>Dikey Görsel</Label>
                <MetaSingleFile
                  type={MetaTypeEnum.SingleImage}
                  onChange={(value) => {
                    this.props.setPageData("profileImageVertical", value);
                  }}
                  meta={{
                    data: this.props.pageData.profileImageVertical,
                    type: MetaTypeEnum.SingleImage,
                    title: "",
                    column: 12,
                    name: "",
                  }}
                  toggleModal={toggleModal}
                />
              </div>
              <div className="item row col-md-12">
                <Label>Varsayılan Video</Label>
                <MetaSingleFile
                  type={MetaTypeEnum.SingleVideo}
                  onChange={(value) => {
                    this.props.setPageData("defaultVideo", value);
                  }}
                  meta={{
                    data: this.props.pageData.defaultVideo,
                    type: MetaTypeEnum.SingleVideo,
                    title: "",
                    column: 12,
                    name: "",
                  }}
                  toggleModal={toggleModal}
                />
              </div>
            </div>
          </FormAccordion>
        </div>
      </div>
    );
  }

  render() {
    return (
      <PageLayout
        history={this.props.history}
        match={this.props.match}
        location={this.props.location}
      >
        <div className="page push-all">
          <PageTitle
            title="Yeni Etkinlik Ekle"
            titleRenderer={() => (
              <span className="edit-title">
                Yönet:
                <strong>Yeni Etkinlik Ekle</strong>
              </span>
            )}
            showBack
            backUrl="/activities"
            history={this.props.history}
            subTitle="Yeni bir etkinlik eklemek için aşağıdaki bilgileri doldurunuz, dilerseniz taslak olarakta oluşturabilirsiniz."
          />
          <PrimaryButton
            onClick={() => {
              this.resetData();
            }}
            iconProps={{ iconName: "Add" }}
            className="absolute-create"
            text="Yeni Etkinlik Ekle"
            styles={{
              root: {
                background: "#62a98e",
                borderColor: "#5b9e85",
                color: "#fff",
              },
              rootHovered: {
                background: "#5b9e85",
                borderColor: "#5b9e85",
              },
              rootDisabled: {
                background: "#ececec",
              },
            }}
          />
          <div className="content row">
            <div className="left col no-padding">
              {this.renderMessageBar()}
              {this.renderTitle()}
              {this.renderForm()}
              {this.renderRules()}
              <Designer defaultCollapsed={false} />
            </div>
            <Sidebar
              categoryBar={{
                hide: true,
              }}
              publishBar={{
                onSave: () => this.onSubmit(),
                disabledSaveButton:
                  this.state.messageType === MessageBarType.success
                    ? true
                    : false,
                hideDeleteButton: true,
                hideDraftButton: true,
                hidePreviewButton: true,
                showOrder: true,
                order: this.props.pageData?.order || 0,
                onChangeOrderIndex: (order) =>
                  this.props.setPageData("order", order),
                showDateOrder: true,
                dateOrder: this.props.pageData?.dateOrder || 0,
                onChangeDateOrderIndex: (order) =>
                  this.props.setPageData("dateOrder", order),
                showIntegration: true,
                integrationId: this.props.pageData?.integrationId || "",
                onChangeIntegrationId: (value: string) =>
                  this.props.setPageData("integrationId", value),
              }}
              layoutBar={{
                hide: true,
              }}
              archiveBar={{
                hide: true,
              }}
              thumbnailBar={{
                hide: true,
              }}
            >
              {this.renderImages()}
            </Sidebar>
          </div>
        </div>
      </PageLayout>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateActivity);
